<template>
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-md-6 col-lg-4">
        <div class="card shadow">
          <div class="card-header text-center bg-primary text-white">
            <h4 class="mb-0">
              <font-awesome-icon icon="receipt" class="me-2" />
              Quittungszugriff
            </h4>
          </div>
          <div class="card-body p-4">
            <!-- Loading State -->
            <div v-if="loading" class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Überprüfe Zugriff...</span>
              </div>
              <p class="mt-3 text-muted">Überprüfe Zugriffsberechtigung...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger" role="alert">
              <font-awesome-icon icon="exclamation-triangle" class="me-2" />
              {{ error }}
            </div>

            <!-- Form -->
            <div v-else>
              <p class="text-muted mb-4">
                Um diese Quittung anzuzeigen, gib bitte die E-Mail-Adresse ein, 
                an die die Quittung gesendet wurde.
              </p>

              <form @submit.prevent="checkAccess">
                <div class="mb-3">
                  <label for="email" class="form-label">E-Mail-Adresse</label>
                  <input
                    id="email"
                    v-model="email"
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': emailError }"
                    placeholder="deine-email@beispiel.de"
                    required
                    :disabled="checking"
                  />
                  <div v-if="emailError" class="invalid-feedback">
                    {{ emailError }}
                  </div>
                </div>

                <div class="d-grid">
                  <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="checking || !email"
                  >
                    <font-awesome-icon 
                      :icon="checking ? 'spinner' : 'check'" 
                      :class="{ 'fa-spin': checking }"
                      class="me-2" 
                    />
                    {{ checking ? 'Überprüfe...' : 'Zugriff überprüfen' }}
                  </button>
                </div>
              </form>

              <div class="text-center mt-4">
                <p class="text-muted small mb-2">Hast du bereits ein Konto?</p>
                <router-link 
                  :to="{ name: 'login', query: { redirect: $route.fullPath } }" 
                  class="btn btn-outline-secondary btn-sm"
                >
                  <font-awesome-icon icon="sign-in-alt" class="me-1" />
                  Anmelden
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { receiptsApi } from '@/api/receipts'
import { showError } from '@/services/notification'

const route = useRoute()
const router = useRouter()

const email = ref('')
const emailError = ref('')
const loading = ref(false)
const checking = ref(false)
const error = ref(null)

const checkAccess = async () => {
  if (!email.value) {
    emailError.value = 'Bitte gib eine E-Mail-Adresse ein'
    return
  }

  emailError.value = ''
  checking.value = true

  try {
    const receiptId = route.params.id
    const response = await receiptsApi.checkReceiptAccess(receiptId, email.value)
    
    if (response.accessGranted) {
      
      const redirectPath = route.query.redirect || { name: 'receipt-detail', params: { id: receiptId } }
      router.push(redirectPath)
    } else {
      error.value = 'Du hast keinen Zugriff auf diese Quittung. Bitte überprüfe die E-Mail-Adresse oder melde dich an.'
    }
  } catch (err) {
    console.error('Error checking receipt access:', err)
    error.value = err.response?.data?.error || 'Fehler bei der Zugriffsüberprüfung'
  } finally {
    checking.value = false
  }
}

onMounted(() => {
  
  if (route.query.email) {
    email.value = route.query.email
  }
})
</script>
